import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaChartBar, FaCalendarAlt, FaFilter } from "react-icons/fa";
import { useClientes } from '../../services/hooks/useClientes';
import { useComedores } from '../../services/hooks/useComedores';
import { reporteApi } from '../../services/api/reporteApi';
import { downloadExcelReport } from '../../services/utils/downloadHelper';
import "react-toastify/dist/ReactToastify.css";

const ReporteConsolidadoConsumosPorComensal = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Hooks para las operaciones de API
  const { comedores, fetchComedoresByProyecto, loading: loadingComedores } = useComedores();
  const { clientes, fetchClientesByProyecto, loading: loadingClientes } = useClientes();

  // Estado del formulario
  const [formData, setFormData] = useState({
    fecha_servicio_desde: "",
    fecha_servicio_hasta: "",
    id_proyecto: selectedProject ? selectedProject.id : "",
    id_cliente: "",
    id_comedor: ""
  });

  // Estado de carga
  const [loading, setLoading] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  // Cargar datos iniciales
  useEffect(() => {
    console.log('🔍 DEBUG - Verificando datos de sesión:');
    console.log('  - currentUser:', currentUser);
    console.log('  - selectedProject:', selectedProject);
    console.log('  - selectedProject.id:', selectedProject?.id);
    
    if (currentUser?.id && selectedProject?.id) {
      console.log('✅ Cargando datos para proyecto:', selectedProject.id);
      fetchData();
    } else {
      console.log('❌ Faltan datos de sesión - no se cargan datos');
    }
  }, [currentUser?.id, selectedProject?.id]);

  // Debug: Monitorear cambios en formData
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const fetchData = useCallback(async () => {
    try {
      console.log('🔄 Cargando datos para proyecto:', selectedProject.id);
      
      const [comedoresResult, clientesResult] = await Promise.all([
        fetchComedoresByProyecto(selectedProject.id),
        fetchClientesByProyecto(selectedProject.id)
      ]);
      
      console.log('📊 Resultados de carga:');
      console.log('  - Comedores cargados:', comedoresResult?.length || 0);
      console.log('  - Clientes cargados:', clientesResult?.length || 0);
      
      // Debug: Ver estructura de clientes
      if (clientesResult && clientesResult.length > 0) {
        console.log('🔍 Estructura del primer cliente:', clientesResult[0]);
      }
      
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      toast.error('Error cargando datos iniciales');
    }
  }, [selectedProject?.id, fetchComedoresByProyecto, fetchClientesByProyecto]);

  // Función para formatear fecha a DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Función para convertir DD/MM/YYYY a YYYY-MM-DD para el backend
  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  // Función para convertir YYYY-MM-DD a DD/MM/YYYY
  const formatDateFromYYYYMMDD = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Manejar cambio de fecha desde el datepicker
  const handleDateChange = (name, value) => {
    if (value) {
      // El datepicker devuelve formato YYYY-MM-DD, convertir directamente a DD/MM/YYYY
      const [year, month, day] = value.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Componente de fecha personalizado - Simple y funcional
  const CustomDateInput = ({ label, name, value, onChange, required = false }) => {
    return (
      <Form.Group className="mb-3" style={{ position: 'relative' }}>
        <Form.Label>
          <FaCalendarAlt className="me-2" />
          {label}
        </Form.Label>
        <Form.Control
          type="date"
          name={name}
          value={value ? formatDateToYYYYMMDD(value) : ''}
          onChange={(e) => handleDateChange(name, e.target.value)}
          required={required}
          style={{ 
            color: 'transparent',
            textShadow: 'none',
            background: 'transparent'
          }}
        />
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '12px',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: '#212529',
            fontSize: '16px'
          }}
        >
          {value || 'DD/MM/YYYY'}
        </div>
        <Form.Text className="text-muted">
          Formato: DD/MM/YYYY
        </Form.Text>
      </Form.Group>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Text input change:', name, 'value:', value);
    
    // Solo actualizar si el valor es diferente al actual
    setFormData(prev => {
      if (prev[name] !== value) {
        return {
          ...prev,
          [name]: value
        };
      }
      return prev;
    });

    // Sincronizar el datepicker correspondiente
    if (name === 'fecha_servicio_desde') {
      syncDatePicker(value, 'fecha_servicio_desde_picker');
    } else if (name === 'fecha_servicio_hasta') {
      syncDatePicker(value, 'fecha_servicio_hasta_picker');
    }
  };

  // Función para sincronizar el datepicker cuando se escribe manualmente
  const syncDatePicker = (textValue, datePickerName) => {
    if (textValue && textValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = textValue.split('/');
      const datePickerValue = `${year}-${month}-${day}`;
      const datePickerElement = document.querySelector(`input[name="${datePickerName}"]`);
      if (datePickerElement) {
        datePickerElement.value = datePickerValue;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fecha_servicio_desde || !formData.fecha_servicio_hasta) {
      toast.error('Por favor seleccione ambas fechas');
      return;
    }

    if (new Date(formatDateToYYYYMMDD(formData.fecha_servicio_desde)) > new Date(formatDateToYYYYMMDD(formData.fecha_servicio_hasta))) {
      toast.error('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setLoading(true);
    try {
      const params = {
        fecha_servicio_desde: formatDateToYYYYMMDD(formData.fecha_servicio_desde),
        fecha_servicio_hasta: formatDateToYYYYMMDD(formData.fecha_servicio_hasta),
        id_proyecto: formData.id_proyecto,
        id_cliente: formData.id_cliente || null,
        id_comedor: formData.id_comedor || null,
      };

      const fileName = `Reporte_Consolidado_Consumos_Comensal_${formatDateToDDMMYYYY(formData.fecha_servicio_desde)}_${formatDateToDDMMYYYY(formData.fecha_servicio_hasta)}.xlsx`;

      await downloadExcelReport(
        reporteApi.descargarReporteConsolidadoConsumosComensal,
        params,
        fileName
      );
      
      toast.success('Reporte generado exitosamente');
    } catch (error) {
      console.error('Error generando reporte:', error);
      toast.error('Error generando el reporte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer />

      <Modal
        show={loading}
        backdrop="static"
        keyboard={false}
        centered
        aria-labelledby="reporte-consolidado-loading-title"
      >
        <Modal.Body className="text-center py-5 px-4">
          <div className="d-flex justify-content-center mb-3">
            <Spinner
              animation="border"
              role="status"
              variant="primary"
              style={{ width: '3.5rem', height: '3.5rem' }}
            />
          </div>
          <FaFileExcel className="text-success mb-3" style={{ fontSize: '2rem' }} aria-hidden />
          <h5 id="reporte-consolidado-loading-title" className="mb-2">
            Generando el reporte Excel
          </h5>
          <p className="text-muted mb-0 small">
            El servidor está preparando el archivo. No cierre esta ventana; con rangos grandes o muchos
            comensales puede tardar varios minutos.
          </p>
        </Modal.Body>
      </Modal>
      
      {/* Header */}
      <div className="bg-primary text-white py-4">
        <Container>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <Button 
                  variant="outline-light" 
                  className="me-3"
                  onClick={() => navigate('/reportes')}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
                <div>
                  <h4 className="mb-0">
                    <FaChartBar className="me-2" />
                    Reporte Consolidado de Consumos por Comensal
                  </h4>
                  <p className="mb-0 opacity-75">Generar reporte detallado de consumos por comensal</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card className="shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <FaFilter className="me-2 text-primary" />
                  Filtros del Reporte
                </h5>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <CustomDateInput
                        label="Fecha Desde"
                        name="fecha_servicio_desde"
                        value={formData.fecha_servicio_desde}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <CustomDateInput
                        label="Fecha Hasta"
                        name="fecha_servicio_hasta"
                        value={formData.fecha_servicio_hasta}
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cliente</Form.Label>
                        <Form.Select
                          name="id_cliente"
                          value={formData.id_cliente}
                          onChange={handleInputChange}
                        >
                          <option value="">Todos los clientes</option>
                          {loadingClientes ? (
                            <option disabled>Cargando clientes...</option>
                          ) : (
                            clientes.map((cliente) => (
                              <option key={cliente.id} value={cliente.id}>
                                {cliente.nombre_cliente || cliente.nombre || `Cliente #${cliente.id}`}
                              </option>
                            ))
                          )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Solo clientes asociados al proyecto actual
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Comedor</Form.Label>
                        <Form.Select
                          name="id_comedor"
                          value={formData.id_comedor}
                          onChange={handleInputChange}
                        >
                          <option value="">Todos los comedores</option>
                          {loadingComedores ? (
                            <option disabled>Cargando comedores...</option>
                          ) : (
                            comedores.map(comedor => (
                              <option key={comedor.id} value={comedor.id}>
                                {comedor.nombre}
                              </option>
                            ))
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-grid">
                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg"
                      disabled={loading}
                      className="d-flex align-items-center justify-content-center"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generando Reporte...
                        </>
                      ) : (
                        <>
                          <FaFileExcel className="me-2" />
                          Generar Reporte Excel
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Información adicional */}
            <Card className="mt-4">
              <Card.Header>
                <h6 className="mb-0">Información del Reporte</h6>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-0">
                  <strong>Nota:</strong> Este reporte genera un archivo Excel con el detalle de consumos por comensal 
                  en el rango de fechas seleccionado. El archivo incluye información detallada de cada comensal 
                  y sus consumos por fecha y servicio.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReporteConsolidadoConsumosPorComensal;
